import React from 'react';
import {connect} from 'react-redux';
import {Route, withRouter} from 'react-router-dom';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import {compose} from 'redux';

import {getAdminLogs} from '../../../api';
import * as colors from '../../../util/colors';
import SettingsTopbar from '../Topbar';
import AccountReplenishmentTab from './AccountReplenishmentTab';
import NewsletterTab from './NewsletterTab';
import SharedAccountsTab from './SharedAccountsTab';
import RentalRolesTab from './RentalRolesTab';
import Paper from '../../common/Paper';
import Button from '../../common/Button';
import Modal, {Close} from '../../common/Modal';
import TabLinkGroup from '../../common/TabLinkGroup';
import TabLink from '../../common/TabLink';
import {Navigation} from '../../Location/LocationCard.styled';

const ADMINISTRATION_ROOT = '/settings/administration';
const ADMINISTATION_ROOT = '/settings/administation';

const formatDateTime = value => {
  if (!value) {
    return '—';
  }

  return new Date(value).toLocaleString('ru-RU');
};

const formatManager = log => {
  const fullName = [log.manager_first_name, log.manager_last_name]
    .filter(Boolean)
    .join(' ')
    .trim();

  if (fullName && log.manager_email) {
    return `${fullName} (${log.manager_email})`;
  }

  return fullName || log.manager_email || log.manager_id || '—';
};

const formatActionType = value => {
  switch (value) {
    case 'payments':
      return 'Платежи';
    case 'users':
      return 'Пользователи';
    default:
      return value || '—';
  }
};

const formatPayload = value => {
  if (!value) {
    return '—';
  }

  if (typeof value === 'string') {
    return value;
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch (e) {
    return '—';
  }
};

function Administration(props) {
  const {loggedIn, role, authorization, history, location} = props;

  React.useEffect(() => {
    if (location.pathname.startsWith(ADMINISTATION_ROOT)) {
      history.replace(location.pathname.replace(ADMINISTATION_ROOT, ADMINISTRATION_ROOT));
    }
  }, [history, location.pathname]);

  if (!loggedIn) {
    history.push('/');
    return null;
  }

  if (role !== 'administrator') {
    history.push('/settings/profile');
    return null;
  }

  return (
    <>
      <SettingsTopbar />
      <Paper padding="0">
        <Navigation>
          <TabLinkGroup>
            <TabLink to={ADMINISTRATION_ROOT}>Админ логи</TabLink>
            <TabLink to={`${ADMINISTRATION_ROOT}/newsletter`}>Рассылка новостей</TabLink>
            <TabLink to={`${ADMINISTRATION_ROOT}/operations`}>Финансовые операции</TabLink>
            <TabLink to={`${ADMINISTRATION_ROOT}/shared-accounts`}>Корпоративные счета</TabLink>
            <TabLink to={`${ADMINISTRATION_ROOT}/rental-roles`}>Роли проката</TabLink>
          </TabLinkGroup>
        </Navigation>
      </Paper>

      <Route
        exact
        path={[ADMINISTRATION_ROOT, ADMINISTATION_ROOT]}
        render={() => <AdminLogsTab authorization={authorization} />}
      />
      <Route
        exact
        path={[`${ADMINISTRATION_ROOT}/newsletter`, `${ADMINISTATION_ROOT}/newsletter`]}
        render={() => <NewsletterTab authorization={authorization} />}
      />
      <Route
        exact
        path={[`${ADMINISTRATION_ROOT}/operations`, `${ADMINISTATION_ROOT}/operations`]}
        render={() => <AccountReplenishmentTab authorization={authorization} />}
      />
      <Route
        exact
        path={[`${ADMINISTRATION_ROOT}/shared-accounts`, `${ADMINISTATION_ROOT}/shared-accounts`]}
        render={() => <SharedAccountsTab authorization={authorization} />}
      />
      <Route
        exact
        path={[`${ADMINISTRATION_ROOT}/rental-roles`, `${ADMINISTATION_ROOT}/rental-roles`]}
        render={() => <RentalRolesTab authorization={authorization} />}
      />
    </>
  );
}

function AdminLogsTab({authorization}) {
  const [logs, setLogs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [selectedLog, setSelectedLog] = React.useState(null);

  const loadLogs = React.useCallback(() => {
    setLoading(true);
    setError('');

    getAdminLogs({authorization})
      .then(async response => {
        if ((response.status >= 200) && (response.status < 300)) {
          return response.json();
        }

        let message = 'Не удалось загрузить админ логи';

        try {
          const data = await response.json();
          message = data.error || message;
        } catch (e) {
          // ignore non-json error body
        }

        throw new Error(message);
      })
      .then((data) => {
        setLogs(Array.isArray(data) ? data : []);
      })
      .catch((e) => {
        setError(e.message || 'Не удалось загрузить админ логи');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [authorization]);

  React.useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  return (
    <Paper padding="0">
      <Toolbar>
        <div>
          <SectionTitle>Журнал ручных действий администратора</SectionTitle>
          <SectionDescription>
            Пополнения, списания, возвраты, блокировки и другие чувствительные изменения.
          </SectionDescription>
        </div>
        <ReloadButton onClick={loadLogs}>
          Обновить
        </ReloadButton>
      </Toolbar>

      {loading && (
        <StateBlock>
          Загрузка логов...
        </StateBlock>
      )}

      {!loading && error && (
        <StateBlock danger>
          {error}
        </StateBlock>
      )}

      {!loading && !error && (logs.length === 0) && (
        <StateBlock>
          Логи пока пусты.
        </StateBlock>
      )}

      {!loading && !error && (logs.length > 0) && (
        <TableScroll>
          <Table>
            <thead>
              <HeadRow>
                <HeadCell width="170px">Создан</HeadCell>
                <HeadCell width="220px">Инициатор</HeadCell>
                <HeadCell width="130px">Тип</HeadCell>
                <HeadCell width="320px">Текст</HeadCell>
                <HeadCell width="140px">Детали</HeadCell>
              </HeadRow>
            </thead>
            <tbody>
              {logs.map(log => (
                <BodyRow key={log.id}>
                  <BodyCell width="170px">{formatDateTime(log.created_at)}</BodyCell>
                  <BodyCell width="220px">{formatManager(log)}</BodyCell>
                  <BodyCell width="130px">
                    <TypeBadge actionType={log.action_type}>
                      {formatActionType(log.action_type)}
                    </TypeBadge>
                  </BodyCell>
                  <BodyCell width="320px">
                    <LogText>{log.text || log.action || '—'}</LogText>
                    {log.action && (log.text !== log.action) && (
                      <LogActionCode>{log.action}</LogActionCode>
                    )}
                  </BodyCell>
                  <BodyCell width="140px">
                    <DetailsButton
                      type="button"
                      onClick={() => setSelectedLog(log)}
                    >
                      Детали
                    </DetailsButton>
                  </BodyCell>
                </BodyRow>
              ))}
            </tbody>
          </Table>
        </TableScroll>
      )}

      {selectedLog && (
        <Modal width="760px" minHeight="auto" maxHeight="85vh" padding="32px">
          <Close onClick={() => setSelectedLog(null)}>&times;</Close>
          <ModalContent>
            <ModalTitle>Детали админ лога</ModalTitle>
            <ModalMeta>
              <ModalMetaLabel>Создан</ModalMetaLabel>
              {formatDateTime(selectedLog.created_at)}
            </ModalMeta>
            <ModalMeta>
              <ModalMetaLabel>Инициатор</ModalMetaLabel>
              {formatManager(selectedLog)}
            </ModalMeta>
            <ModalMeta>
              <ModalMetaLabel>Тип</ModalMetaLabel>
              {formatActionType(selectedLog.action_type)}
            </ModalMeta>
            <ModalMeta>
              <ModalMetaLabel>Текст</ModalMetaLabel>
              {selectedLog.text || '—'}
            </ModalMeta>
            <ModalMeta>
              <ModalMetaLabel>Action</ModalMetaLabel>
              <ModalCode>{selectedLog.action || '—'}</ModalCode>
            </ModalMeta>
            <ModalPayloadBlock>
              <ModalMetaLabel>Payload</ModalMetaLabel>
              <ModalPayload>{formatPayload(selectedLog.payload)}</ModalPayload>
            </ModalPayloadBlock>
          </ModalContent>
        </Modal>
      )}
    </Paper>
  );
}

const Toolbar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 24px;
  border-bottom: 1px solid ${colors.LINE};
`;

const SectionTitle = styled.h2`
  margin: 0 0 6px;
  font-size: 20px;
  line-height: 1.2;
  color: ${colors.TEXT_TITLE};
`;

const SectionDescription = styled.div`
  color: ${colors.TEXT_MINOR};
  font-size: 14px;
  line-height: 1.5;
`;

const ReloadButton = styled(Button)`
  flex-shrink: 0;
`;

const StateBlock = styled.div`
  padding: 24px;
  color: ${props => props.danger ? colors.RED_MAIN : colors.TEXT_MINOR};
`;

const TableScroll = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
`;

const HeadRow = styled.tr`
  border-bottom: 1px solid ${colors.LINE};
`;

const HeadCell = styled.th`
  padding: 16px 24px;
  width: ${props => props.width || 'auto'};
  text-align: left;
  font-size: 13px;
  font-weight: bold;
  color: ${colors.TEXT_MINOR};
  white-space: nowrap;
`;

const BodyRow = styled.tr`
  border-bottom: 1px solid ${colors.LINE};
`;

const BodyCell = styled.td`
  width: ${props => props.width || 'auto'};
  padding: 16px 24px;
  vertical-align: top;
  font-size: 14px;
  color: ${colors.TEXT_MAIN};
`;

const DetailsButton = styled.button`
  min-height: 36px;
  padding: 0 14px;
  border: 1px solid ${colors.LINE};
  border-radius: 8px;
  background: white;
  color: ${colors.TEXT_MAIN};
  font-size: 13px;
  font-weight: bold;
  cursor: pointer;

  &:hover {
    border-color: ${colors.BUTTON_BLUE};
    color: ${colors.BUTTON_BLUE};
  }
`;

const LogText = styled.div`
  color: ${colors.TEXT_MAIN};
  line-height: 1.5;
`;

const LogActionCode = styled.div`
  margin-top: 6px;
  color: ${colors.TEXT_MINOR};
  font-family: monospace;
  font-size: 12px;
  line-height: 1.4;
`;

const TypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  background-color: ${props => props.actionType === 'payments' ? '#EAF3FF' : '#EEF8E7'};
  color: ${props => props.actionType === 'payments' ? colors.BLUE_MAIN : colors.GREEN_DARK};
  font-size: 12px;
  font-weight: bold;
  line-height: 1;
  white-space: nowrap;
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ModalTitle = styled.h3`
  margin: 0;
  padding-right: 24px;
  font-size: 22px;
  line-height: 1.2;
  color: ${colors.TEXT_TITLE};
`;

const ModalMeta = styled.div`
  color: ${colors.TEXT_MAIN};
  font-size: 14px;
  line-height: 1.6;
`;

const ModalMetaLabel = styled.div`
  margin-bottom: 6px;
  color: ${colors.TEXT_MINOR};
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const ModalCode = styled.div`
  font-family: monospace;
  color: ${colors.TEXT_MAIN};
  word-break: break-word;
`;

const ModalPayloadBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ModalPayload = styled.pre`
  margin: 0;
  padding: 16px;
  border-radius: 10px;
  background: ${colors.BG};
  color: ${colors.TEXT_MAIN};
  font-family: monospace;
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  overflow: auto;
`;

Administration.propTypes = {
  loggedIn: PropTypes.bool,
  role: PropTypes.string,
  authorization: PropTypes.string,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    replace: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
};

Administration.defaultProps = {
  loggedIn: false,
  role: '',
  authorization: '',
};

AdminLogsTab.propTypes = {
  authorization: PropTypes.string,
};

AdminLogsTab.defaultProps = {
  authorization: '',
};

const mapStateToProps = ({manager}) => ({
  loggedIn: Boolean(manager.accessToken && manager.data && Object.keys(manager.data).length > 0),
  role: manager.data?.role,
  authorization: manager.accessToken && `Bearer ${manager.accessToken}`,
});

export default compose(
  connect(mapStateToProps),
  withRouter
)(Administration);
